import { useState, Fragment } from "react";
import { Button, Row, Stack } from "react-bootstrap";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Search01Icon,
  Building03FreeIcons,
} from "@hugeicons/core-free-icons/index";
import { useTranslation } from "react-i18next";
import { ArrivalCard } from "../Reception/ArrivalCard";
import {
  useAllInsepctionOrders,
  useUpdateHousingInspectionOrderStatus,
} from "@/services/houseInspections";
import { useTableFilter } from "@/utils/hooks/useFilter";
import { Divider, Flex, Tag } from "antd";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { Link, useLocation, useSearchParams } from "react-router";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { AntInput } from "@/CommonElements/AntInput";
import dayjs from "dayjs";
import { LabelValue } from "@/Component/Value";
import { Col, Spinner } from "reactstrap";
import { CountModal } from "@/Component/CountModal";
import { AntPagination } from "@/Component/AntPagination/AntPagination";
import { EXAMINATION_TYPES } from "@/utils/constants";
import { useSystem } from "@/utils/hooks/useLocation";

export const HousingInspectionOrders = ({ activeTab }) => {
  const { t } = useTranslation();
  const { getLocalizedText, getLocalizedNumber } = useLocalizedText();
  const { filters } = useTableFilter({});
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );

  const [searchParams] = useSearchParams();
  let examination_status = searchParams.get("tab") || "";
  const { newOrder } = useLocation().state || {};
  examination_status = examination_status === "all" ? "" : examination_status;

  const { city_id } = useSystem();

  const [currentPage, setCurrentPage] = useState(1);
  const {
    data: InspectionOrdersRes,
    isLoading,
    isRefetching,
  } = useAllInsepctionOrders({
    ...filters,
    page: currentPage,
    per_page: 10,
    examination_status,
    search: debouncedValue,
    city_id,
    active: activeTab,
  });

  const {
    mutate: updateHousingInspectionOrderStatus,
    variables,
    isPending,
  } = useUpdateHousingInspectionOrderStatus({
    ...filters,
    page: currentPage,
    per_page: 10,
    examination_status,
    search: debouncedValue,
  });

  const InspectionData = InspectionOrdersRes?.data?.data ?? [];
  const meta = InspectionOrdersRes?.data?.meta;

  const mapTagColor = {
    [EXAMINATION_TYPES.complaint]: "red",
    [EXAMINATION_TYPES.normal]: "gray",
    [EXAMINATION_TYPES.track]: "blue",
  };
  const labelValues = [
    {
      key: "examination_type",
      label: t("HOUSING_INSPECTION_ORDER_TYPE"),
      render: (value) => (
        <Tag color={mapTagColor[value?.value]}>
          {getLocalizedText(value?.label_ar, value?.label_en)}
        </Tag>
      ),
    },
    {
      key: "period_code",
      label: t("HOUSING_INSPECTION_ORDER_CODE"),
      render: (value) => getLocalizedNumber(value?.code),
    },
    {
      key: "examination_date",
      label: t("HOUSING_INSPECTION_ORDER_DATE"),
      render: (value) =>
        value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      key: "examination_period",
      label: t("HOUSING_INSPECTION_ORDER_PERIOD"),
      render: (value) => (
        <Stack direction="horizontal" gap={2} className="flex-wrap">
          <span>
            {/* {value?.id} - */}
            {getLocalizedText(value?.name_ar, value?.name_en)}
          </span>
          {value?.time_to && value?.time_from && (
            <Flex gap={2} align="center" wrap>
              <span dir="ltr" className="text-uppercase">
                {value?.time_to
                  ? dayjs(value?.time_to, "HH:mm").format("hh:mm a")
                  : "-"}
              </span>
              -
              <span dir="ltr" className="text-uppercase">
                {value?.time_from
                  ? dayjs(value?.time_from, "HH:mm").format("hh:mm a")
                  : "-"}
              </span>
            </Flex>
          )}
        </Stack>
      ),
    },
    {
      key: "assigned_users",
      label: t("HOUSING_INSPECTION_ORDER_MEMBER_1"),
      render: (value) =>
        getLocalizedText(value?.[0]?.name_ar, value?.[0]?.name_en),
    },
    {
      key: "assigned_users",
      label: t("HOUSING_INSPECTION_ORDER_MEMBER_2"),
      render: (value) =>
        getLocalizedText(value?.[1]?.name_ar, value?.[1]?.name_en),
    },
    {
      key: "houses",
      label: t("BUILDINGS"),
      render: (value) => {
        const count = value.length;
        return (
          <CountModal
            count={count}
            modal={{
              props: {
                size: "lg",
                centered: true,
              },
              header: {
                children: t("BUILDINGS"),
              },
              body: {
                children: (
                  <>
                    {value?.map((h) => (
                      <Row className="p-3 border-bottom" key={h.id}>
                        <COL>
                          <LABEL_VALUE
                            label={t("BUILDING_NAME")}
                            value={h?.houseCommercialNameAr}
                          />
                        </COL>
                        <COL>
                          <LABEL_VALUE
                            label={t("ADDRESS")}
                            value={
                              h?.address ||
                              [h.houseCommercialNameAr, h.cityName].join(" - ")
                            }
                          />
                        </COL>
                        <COL>
                          <LABEL_VALUE
                            label={t("PERMIT_NUMBER")}
                            value={h?.permit_no || "tttttt-"}
                          />
                        </COL>
                      </Row>
                    ))}
                  </>
                ),
              },
            }}
          >
            {[value[0]?.house_id, value[0]?.houseCommercialNameAr].join(" - ")}
          </CountModal>
        );
      },
    },
  ];

  // Create action buttons for each arrival
  const createActions = (item, status) => {
    if (status?.value === "in_progress") {
      return (
        <Link
          to={`${item.id}/buildings`}
          className="btn btn-outline btn-brand flex-fill"
        >
          {t("COMPLETE_INSPECTION")}
        </Link>
      );
    }

    if (status?.value === "completed") {
      return (
        <Link
          to={`${item.id}/buildings`}
          className="btn btn-outline btn-brand flex-fill"
        >
          {t("VIEW")}
        </Link>
      );
    }

    return (
      <>
        <Button
          disabled={variables?.order_number === item.id && isPending}
          onClick={() => {
            updateHousingInspectionOrderStatus({
              order_number: item.id,
              examination_status: "in_progress",
            });
          }}
          className="btn btn-primary flex-fill"
        >
          {variables?.order_number === item.id && isPending && (
            <Spinner size="sm" />
          )}
          {t("START_INSPECTION")}
        </Button>
      </>
    );
  };
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  const skeletonItems = Array.from({ length: 3 }, (_, index) => ({
    id: index,
  }));

  return (
    <div>
      <Row className="flex-wrap">
        <Stack direction="horizontal" gap={2}>
          <AntInput
            prefix={<HugeiconsIcon icon={Search01Icon} />}
            placeholder={t("SEARCH_HERE")}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value || "")}
          />

          {/* <CreateExaminationOrder /> */}
        </Stack>
        {isLoading ? (
          skeletonItems.map((item) => (
            <ArrivalCard loading={isLoading} arrival={null} key={item.id} />
          ))
        ) : !InspectionData?.length ? (
          <div className="empty-state text-center py-5 w-100">
            <div className="empty-state-icon mb-3">
              <HugeiconsIcon
                icon={Building03FreeIcons}
                size={48}
                className="text-muted"
              />
            </div>
            <h4 className="empty-state-title mb-2">
              {t("NO_HOUSING_INSPECTION_ORDERS_FOUND")}
            </h4>
            <p className="empty-state-description text-muted mb-4">
              {debouncedValue
                ? t("NO_HOUSING_INSPECTION_ORDERS_SEARCH_DESCRIPTION")
                : t("NO_HOUSING_INSPECTION_ORDERS_DESCRIPTION")}
            </p>
          </div>
        ) : (
          <>
            {InspectionData?.map((item, index) => {
              const status =
                item?.examination_status || item?.examination_result;

              return (
                <Fragment key={Object.values(item).join("-")}>
                  {index === 0 && isRefetching && newOrder && (
                    <ArrivalCard loading={true} arrival={null} />
                  )}
                  <ArrivalCard
                    title={`${t("HOUSING_INSPECTION_ORDERS")}: `}
                    titleValue={item.order_code || "-"}
                    arrival={item}
                    statusConfig={getExaminationResultStatus(
                      status.value,
                      getLocalizedText(status?.label_ar, status?.label_en),
                      t("NOT_INSPECTED")
                    )}
                    actions={createActions(item, status)}
                    labelValues={labelValues}
                  />
                </Fragment>
              );
            })}
          </>
        )}
      </Row>
      <div className="sticky-bottom z-1 w-100  bg-white">
        <AntPagination
          className="p-3"
          hideOnSinglePage
          pageSize={10}
          current={currentPage}
          onChange={handlePageChange}
          total={meta?.total ?? 0}
          showSizeChanger={false}
        />
      </div>
    </div>
  );
};
const COL = ({ children }) => (
  <Col xs={6} md={6} className="mb-2">
    {children}
  </Col>
);
const LABEL_VALUE = (props) => <LabelValue direction="vertical" {...props} />;

export const getExaminationResultStatus = (status, text, defaultText) => {
  const statusMap = {
    completed: {
      color: "success",
      text,
    },
    qualified: {
      color: "success",
      text,
    },
    not_qualified: {
      color: "danger",
      text,
    },
    not_scheduled: {
      color: "danger",
      text,
    },
    in_progress: {
      color: "brand",
      text,
    },
  };

  return (
    statusMap[status] || {
      color: "brand",
      text: defaultText,
    }
  );
};
